import React from 'react';
import {
  Phone, Mail, MapPin, ArrowLeft
} from 'lucide-react';
import CreateAuthorizationModal from './request-detail/CreateAuthorizationModal';
import SendPreviewModal from './request-detail/SendPreviewModal';
import WorkflowSection from './request-detail/WorkflowSection';
import ReceivedAndReopenSection from './request-detail/ReceivedAndReopenSection';
import ActivityLogSection from './request-detail/ActivityLogSection';
import useRequestDetail from './useRequestDetail';

// Request Detail Component
// Request Detail Component
/** Purpose: Renders request detail. Inputs: { request, authorizations, onBack, onUpdated, onDeleted, onRefresh, onEdit }. Output: React render output (JSX). */
export default function RequestDetail({ request, authorizations, onBack, onUpdated, onDeleted, onRefresh, onEdit }) {
  const {
    // State values
    isUpdating,
    showStatusUpdate,
    showCreateAuth,
    authStep,
    signatureData,
    showSignaturePad,
    justCreated,
    authFlowStatus,
    authFlowStep,
    authFlowError,
    authFlowAuthorizationId,
    statusError,
    denialReason,
    sendError,
    gmailDraftError,
    gmailDraftLoading,
    showSendPreview,
    emailTo,
    emailSubject,
    emailMessage,
    testEmail,
    testSending,
    testSendError,
    testSendSuccess,
    noteText,
    noteError,
    noteSaving,
    portalLink,
    portalPin,
    portalError,
    portalLoading,
    portalCopied,
    showDevTestSend,
    showReopenPanel,
    reopenMessage,
    reopenSendFollowUp,
    reopenLoading,
    reopenError,

    // Setters
    setShowStatusUpdate,
    setShowCreateAuth,
    setAuthStep,
    setSignatureData,
    setShowSignaturePad,
    setStatusError,
    setDenialReason,
    setSendError,
    setGmailDraftError,
    setShowSendPreview,
    setEmailTo,
    setEmailSubject,
    setEmailMessage,
    setTestEmail,
    setTestSendError,
    setTestSendSuccess,
    setShowDevTestSend,
    setShowReopenPanel,
    setReopenMessage,
    setReopenSendFollowUp,
    setNoteText,
    setNoteError,

    // Derived values
    resolvedAuthorizationId,
    revocationContact,
    statusConfig,
    recordTypeLabels,
    canComposeEmail,
    isManualDelivery,
    hasPortalLink,
    isDev,
    daysSinceSent,
    needsFollowUp,
    timelineStages,
    currentTimelineIndex,
    nextAction,
    activeAuthorizations,

    // Handlers
    buildEmailDraft,
    handleCreateAndAttachAuthorization,
    handleResumeAuthorization,
    handleRestartAuthorization,
    handleAttachExistingAuthorization,
    handleSend,
    handleCreateGmailDraft,
    handleSendViaGmail,
    handleConfirmSentManual,
    handleTestSend,
    handleStatusUpdate,
    handleAddNote,
    handleLogFollowUp,
    handleReopen,
    handleGeneratePortalLink,
    handleCopy,
    handleDownloadPackage,
    handleDeleteRequest,
  } = useRequestDetail(request, authorizations, onUpdated, onRefresh, onDeleted);

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to requests
      </button>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">{request.providerName}</h3>
              <p className="text-white/80">{request.organization}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onEdit}
                disabled={isUpdating || ['RECEIVED', 'PARTIALLY_RECEIVED', 'DENIED', 'CANCELLED'].includes(request.status)}
                className="px-3 py-1.5 bg-white/10 text-white rounded-full text-xs font-medium hover:bg-white/20 disabled:opacity-50"
                title={['RECEIVED', 'PARTIALLY_RECEIVED', 'DENIED', 'CANCELLED'].includes(request.status) ? 'Closed requests cannot be edited' : 'Edit request'}
              >
                Edit Request
              </button>
              <button
                onClick={handleDeleteRequest}
                disabled={isUpdating}
                className="px-3 py-1.5 bg-white/10 text-white rounded-full text-xs font-medium hover:bg-white/20 disabled:opacity-50"
              >
                Delete Request
              </button>
              <span className="px-3 py-1 bg-white/20 text-white rounded-full text-sm font-medium">
                {statusConfig.label}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Provider Info */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-slate-900 mb-3">Provider Details</h4>
              <div className="space-y-2 text-sm">
                {request.department && (
                  <p className="text-slate-600">Department: {request.department}</p>
                )}
                {request.address && (
                  <p className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4" />
                    {request.address}, {request.city}, {request.state} {request.postalCode}
                  </p>
                )}
                {request.phone && (
                  <p className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4" />
                    {request.phone}
                  </p>
                )}
                {request.email && (
                  <p className="flex items-center gap-2 text-slate-600">
                    <Mail className="w-4 h-4" />
                    {request.email}
                  </p>
                )}
                {request.providerNpi && (
                  <p className="text-slate-600">
                    NPI: {request.providerNpi}
                    {request.npiVerified && <span className="text-green-600 ml-2">✓ Verified</span>}
                  </p>
                )}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-slate-900 mb-3">Request Details</h4>
              <div className="space-y-2 text-sm">
                <p className="text-slate-600">
                  <strong>Record Types:</strong> {recordTypeLabels || 'N/A'}
                </p>
                {request.dateRangeStart && (
                  <p className="text-slate-600">
                    <strong>Date Range:</strong> {new Date(request.dateRangeStart).toLocaleDateString()} - {request.dateRangeEnd ? new Date(request.dateRangeEnd).toLocaleDateString() : 'Present'}
                  </p>
                )}
                <p className="text-slate-600">
                  <strong>Urgency:</strong> {request.urgency}
                </p>
                <p className="text-slate-600">
                  <strong>Delivery:</strong> {request.deliveryMethod}
                </p>
                {(request.destinationProviderName || request.destinationOrganization || request.destinationEmail) && (
                  <p className="text-slate-600">
                    <strong>Deliver To:</strong>{' '}
                    {[request.destinationProviderName, request.destinationOrganization].filter(Boolean).join(' - ') || 'Destination provider'}
                  </p>
                )}
                {request.sendCopyToUser && (
                  <p className="text-slate-600">
                    <strong>Copy to User:</strong> Yes
                  </p>
                )}
              </div>
            </div>
          </div>

          {request.specificRecords && (
            <div>
              <h4 className="font-medium text-slate-900 mb-2">Specific Records Requested</h4>
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{request.specificRecords}</p>
            </div>
          )}

          {request.notes && (
            <div>
              <h4 className="font-medium text-slate-900 mb-2">Additional Notes</h4>
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{request.notes}</p>
            </div>
          )}

          {(request.destinationProviderName || request.destinationOrganization || request.destinationEmail || request.destinationFax) && (
            <div>
              <h4 className="font-medium text-slate-900 mb-2">Destination Provider</h4>
              <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-700 space-y-1">
                <p>{[request.destinationProviderName, request.destinationOrganization].filter(Boolean).join(' - ')}</p>
                {request.destinationDepartment && <p>{request.destinationDepartment}</p>}
                {(request.destinationAddress || request.destinationCity || request.destinationState || request.destinationPostalCode) && (
                  <p>
                    {[request.destinationAddress, request.destinationCity, request.destinationState, request.destinationPostalCode]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                )}
                {request.destinationEmail && <p>Email: {request.destinationEmail}</p>}
                {request.destinationFax && <p>Fax: {request.destinationFax}</p>}
                {request.destinationPhone && <p>Phone: {request.destinationPhone}</p>}
              </div>
            </div>
          )}

          <WorkflowSection
            request={request}
            timelineStages={timelineStages}
            currentTimelineIndex={currentTimelineIndex}
            nextAction={nextAction}
            justCreated={justCreated}
            authFlowStatus={authFlowStatus}
            authFlowStep={authFlowStep}
            authFlowError={authFlowError}
            authFlowAuthorizationId={authFlowAuthorizationId}
            isUpdating={isUpdating}
            activeAuthorizations={activeAuthorizations}
            onOpenCreateAuth={() => setShowCreateAuth(true)}
            onResumeAuthorization={handleResumeAuthorization}
            onRestartAuthorization={handleRestartAuthorization}
            onAttachExistingAuthorization={handleAttachExistingAuthorization}
            canComposeEmail={canComposeEmail}
            isManualDelivery={isManualDelivery}
            hasPortalLink={hasPortalLink}
            onDownloadPackage={handleDownloadPackage}
            onOpenSendPreview={() => setShowSendPreview(true)}
            sendError={sendError}
            portalLoading={portalLoading}
            onGeneratePortalLink={handleGeneratePortalLink}
            canGeneratePortalLink={Boolean(resolvedAuthorizationId)}
            portalError={portalError}
            portalLink={portalLink}
            portalPin={portalPin}
            portalCopied={portalCopied}
            onCopy={handleCopy}
            needsFollowUp={needsFollowUp}
            daysSinceSent={daysSinceSent}
            onLogFollowUp={handleLogFollowUp}
            noteSaving={noteSaving}
            showStatusUpdate={showStatusUpdate}
            setShowStatusUpdate={setShowStatusUpdate}
            statusError={statusError}
            onClearStatusError={() => setStatusError('')}
            onStatusUpdate={handleStatusUpdate}
            denialReason={denialReason}
            setDenialReason={setDenialReason}
            isDev={isDev}
            showDevTestSend={showDevTestSend}
            setShowDevTestSend={setShowDevTestSend}
            testEmail={testEmail}
            setTestEmail={setTestEmail}
            testSendError={testSendError}
            setTestSendError={setTestSendError}
            testSendSuccess={testSendSuccess}
            setTestSendSuccess={setTestSendSuccess}
            testSending={testSending}
            onTestSend={handleTestSend}
            buildEmailDraft={buildEmailDraft}
            setEmailTo={setEmailTo}
            setEmailSubject={setEmailSubject}
            setEmailMessage={setEmailMessage}
            setSendError={setSendError}
            setGmailDraftError={setGmailDraftError}
          />

          <ReceivedAndReopenSection
            request={request}
            showReopenPanel={showReopenPanel}
            setShowReopenPanel={setShowReopenPanel}
            reopenMessage={reopenMessage}
            setReopenMessage={setReopenMessage}
            reopenSendFollowUp={reopenSendFollowUp}
            setReopenSendFollowUp={setReopenSendFollowUp}
            reopenError={reopenError}
            reopenLoading={reopenLoading}
            onReopen={handleReopen}
          />

          <ActivityLogSection
            request={request}
            noteText={noteText}
            setNoteText={setNoteText}
            noteError={noteError}
            setNoteError={setNoteError}
            noteSaving={noteSaving}
            onAddNote={handleAddNote}
          />
        </div>
      </div>

      <CreateAuthorizationModal
        open={showCreateAuth}
        authStep={authStep}
        setAuthStep={setAuthStep}
        authFlowStatus={authFlowStatus}
        authFlowError={authFlowError}
        request={request}
        recordTypeLabels={recordTypeLabels}
        revocationContact={revocationContact}
        signatureData={signatureData}
        setSignatureData={setSignatureData}
        showSignaturePad={showSignaturePad}
        setShowSignaturePad={setShowSignaturePad}
        isUpdating={isUpdating}
        onClose={() => setShowCreateAuth(false)}
        onCreateAndAttach={handleCreateAndAttachAuthorization}
      />

      <SendPreviewModal
        open={showSendPreview}
        request={request}
        recordTypeLabels={recordTypeLabels}
        canComposeEmail={canComposeEmail}
        emailTo={emailTo}
        setEmailTo={setEmailTo}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        emailMessage={emailMessage}
        setEmailMessage={setEmailMessage}
        sendError={sendError}
        gmailDraftError={gmailDraftError}
        onClose={() => setShowSendPreview(false)}
        onDownloadPackage={handleDownloadPackage}
        onConfirmSentManual={handleConfirmSentManual}
        onCreateGmailDraft={handleCreateGmailDraft}
        onSendViaGmail={handleSendViaGmail}
        onSend={handleSend}
        hasPortalLink={hasPortalLink}
        isUpdating={isUpdating || gmailDraftLoading}
      />
    </div>
  );
}
